import * as React from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import omit from 'lodash/omit'

import i18n from 'modules/core/lib/i18n'

import WorksheetButtons from 'modules/payroll_bundle/components/common/components/WorksheetButtons'
import type {
  SupplementType,
  SupplementsMatches,
} from 'modules/payroll_bundle/components/common/helper/worksheet'
import type { VisibleButtons } from 'modules/payroll_bundle/components/common/helper/worksheet'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'

import AssignSupplementTypeModal from './AssignSupplementTypeModal'

interface Props {
  visibleButtons: VisibleButtons[]
  setVisibleButtons: Dispatch<SetStateAction<VisibleButtons[]>>
}

const WorksheetHeader = ({ visibleButtons, setVisibleButtons }: Props) => {
  const {
    supplementTypes,
    editingSupplementType,
    activeButton,
    showAssignTypeModal,
    worksheetData,
    worksheetMatches,
    supplementDispatch,
  } = useBulkImportSupplements()

  const setActiveButton = (value: string) => {
    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        activeButton: value,
      },
    })
  }

  const setWorksheetMatches = (worksheetMatches: SupplementsMatches) => {
    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        worksheetMatches,
      },
    })
  }

  const buildSupplementTypeButtonOptions = (button: SupplementType) => {
    const option1 = worksheetMatches[button.id]
      ? {
          onClick: () => setWorksheetMatches(omit(worksheetMatches, button.id)),
          title: i18n.t('payroll_bundle.import_supplements.upload.remove_data_column'),
        }
      : {
          onClick: () => setActiveButton(button.id),
          title: i18n.t('payroll_bundle.import_supplements.upload.select_data_column'),
        }

    return [
      option1,
      {
        onClick: () => {
          supplementDispatch({
            type: UPDATE_DATA,
            payload: {
              showAssignTypeModal: true,
              editingSupplementType: supplementTypes.find((s) => s.id === button.id),
            },
          })
        },
        title: i18n.t('payroll_bundle.import_supplements.upload.edit_supplement_titles'),
      },
    ]
  }

  const selectDataColumnPrompt = () => {
    if (activeButton === '') {
      return i18n.t('payroll_bundle.import_supplements.upload.select_data_prompt')
    }

    const supplementTypeLabel = supplementTypes.find((b) => b.id === activeButton)?.label
    const buttonName = (supplementTypeLabel || activeButton).toUpperCase()

    return i18n.t('payroll_bundle.import_supplements.upload.select_column_prompt', {
      buttonName,
    })
  }

  const matchComplete = (): boolean => {
    const requiredColumns = visibleButtons
      .filter((item) => item.required)
      .map((item) => Object.keys(item)[0]) as string[]

    const worksheetMatchedColumns = Object.keys(worksheetMatches)

    return requiredColumns.every((column: string) => worksheetMatchedColumns.includes(column))
  }

  const addData = () =>
    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        showAssignTypeModal: true,
        editingSupplementType: null,
      },
    })

  return (
    <>
      <WorksheetButtons
        addData={addData}
        activeButton={activeButton}
        matchComplete={matchComplete()}
        worksheetData={worksheetData}
        visibleButtons={visibleButtons}
        setVisibleButtons={setVisibleButtons}
        supplementTypes={supplementTypes}
        buildSupplementTypeButtonOptions={buildSupplementTypeButtonOptions}
        setActiveButton={setActiveButton}
        worksheetMatches={worksheetMatches}
        setWorksheetMatches={setWorksheetMatches}
      />
      <Box
        background="viridian200"
        height="s48"
        alignItems="center"
        flexDirection="row"
        paddingX="s24"
        gap="s12"
      >
        <Icon.Medium icon="info-circle-line" color="grey800" />
        <Text.Normal size="300" weight="medium">
          {selectDataColumnPrompt()}
        </Text.Normal>
      </Box>
      {showAssignTypeModal && <AssignSupplementTypeModal supplementType={editingSupplementType} />}
    </>
  )
}

export default WorksheetHeader
